import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import type { ChartData, ChartOptions, ScriptableContext } from 'chart.js';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Typography, formatDate } from 'enable-ui';

import {
  FunctionalAbilityTableBody,
  FunctionalAbilityTableHeader,
} from 'modules/learners/styles/common';

import type { FropResult, FropResultReport } from '../core/type';

import { cloneDeep } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { getFropResult } from '../core/services';
import { FropResultLevel } from './FropResultLevel';

interface Props {
  visible: boolean;
  learnerId: string;
  showDetail: boolean;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

export const FropResultDrawer = ({ visible, learnerId, showDetail }: Props) => {
  const [fropResult, setFropResult] = useState<FropResultReport>({
    questions: [],
    results: [],
  });
  const questions = fropResult?.questions || [];
  const results = fropResult?.results || [];
  const chartResults = cloneDeep(results).reverse();

  const [detail, setDetail] = useState<FropResult | null>(null);
  const maxScore = Math.max(...results.map((el) => el.score));

  const options: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        min: 0,
        max: Math.round(maxScore / 10) * 10 + 10,
        ticks: {
          stepSize: 10,
          color: '#808080',
        },
      },
      x: {
        ticks: {
          maxTicksLimit: 8,
          color: '#808080',
        },
        grid: {
          display: false,
        },
      },
    },
  };

  const labels = chartResults.map((el) => formatDate(el.createdAt, 'dd MMM'));

  const data: ChartData<'line', number[], string> = {
    labels,
    datasets: [
      {
        fill: true,
        label: 'Score',
        data: chartResults.map((el) => el.score),
        borderColor: '#383ECD',
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 200);

          gradient.addColorStop(0, 'rgba(182, 190, 242, 1)');
          gradient.addColorStop(1, 'rgba(182, 190, 242, 0.00)');

          return gradient;
        },
      },
    ],
  };

  useEffect(() => {
    if (visible) {
      getFropResult(learnerId, (rs) => {
        setFropResult(rs);

        if (rs?.results?.length && showDetail) {
          setDetail(rs.results[0]);
        }
      });
    } else {
      setDetail(null);
    }
  }, [visible]);

  return (
    <div
      style={{
        overflow: 'hidden',
        display: 'flex',
        width: detail ? 1120 : 480,
        transition: 'all 0.5s',
      }}
    >
      <div style={{ padding: 32, maxHeight: '100vh', overflow: 'auto', minWidth: 480 }}>
        <Typography variant="h3" color={COLOR.surfaceNeutralBold['neutral-11']} weight={500}>
          Falls Risk for Older People history
        </Typography>
        <div className="border-container p-16" style={{ margin: '40px 0' }}>
          <Line options={options} data={data} />
        </div>
        <div className="border-container p-16">
          <FunctionalAbilityTableHeader>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
                flex: 1,
              }}
            >
              Score
            </Typography>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
                flex: 1,
              }}
            >
              Fall risk
            </Typography>
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 600,
                flex: 1,
              }}
            >
              Completed on
            </Typography>
          </FunctionalAbilityTableHeader>
          {results.map((el, index) => (
            <FunctionalAbilityTableBody
              $bgColor={`${
                el.id === detail?.id
                  ? 'rgba(0, 0, 0, 0.05)'
                  : (index + 1) % 2 === 0
                    ? COLOR_CONFIG.doctor
                    : COLOR_CONFIG.white
              }`}
              className="bg-hover"
              onClick={() => setDetail(el)}
            >
              <Typography
                style={{
                  color: '#808080',
                  fontSize: 18,
                  fontWeight: 400,
                  flex: 1,
                }}
              >
                {el.score}
              </Typography>
              <FropResultLevel score={el.score} />
              <Typography
                style={{
                  color: '#808080',
                  fontSize: 18,
                  fontWeight: 400,
                  flex: 1,
                }}
              >
                {formatDate(el.createdAt, 'dd MMM yyyy')}
              </Typography>
            </FunctionalAbilityTableBody>
          ))}
        </div>
      </div>
      {detail ? (
        <div
          style={{
            padding: 32,
            maxHeight: '100vh',
            overflow: 'auto',
            minWidth: 640,
            minHeight: '100vh',
            borderLeft: '1px solid rgba(0, 0, 0, 0.25)',
          }}
        >
          <Typography
            style={{
              color: '#222',
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Clinical Frailty Scale result
          </Typography>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              marginTop: 6,
              marginBottom: 24,
            }}
          >
            <Typography
              style={{
                color: '#222',
                fontSize: 26,
                fontWeight: 500,
                marginRight: 12,
              }}
            >
              {detail.score}
            </Typography>
            <FropResultLevel score={detail.score} />
            <Typography
              style={{
                color: '#808080',
                fontSize: 16,
                fontWeight: 400,
                marginLeft: 'auto',
              }}
            >
              {formatDate(detail.createdAt, 'dd MMM yyy')}
            </Typography>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {questions.map((el, idx) => (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: 12,
                  border: '1px solid rgba(0, 0, 0, 0.25)',
                }}
              >
                <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
                  {idx + 1}. {el.question}
                </Typography>
                <Typography
                  style={{
                    color: COLOR_CONFIG['neutral-text'],
                    fontWeight: 600,
                    marginTop: 24,
                  }}
                  variant="body-3"
                >
                  Answer
                </Typography>
                {el.component ? (
                  <Typography
                    style={{
                      color: detail.answers.find((d) => d.healthFropQuestion?.id === el.id)?.answer
                        ? COLOR.surfaceNeutralBold['neutral-11']
                        : COLOR_CONFIG['neutral-text'],
                    }}
                    variant="body-2"
                  >
                    {detail.answers.find((d) => d.healthFropQuestion?.id === el.id)?.answer ||
                      'No answer'}
                  </Typography>
                ) : null}
                {el.items.length ? (
                  <ul
                    style={{
                      paddingLeft: 10,
                      marginTop: 4,
                    }}
                  >
                    {el.items.map((e) => {
                      const answer = detail.answers.find((d) => d.healthFropQuestion?.id === e.id);

                      return (
                        <li style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          &#8226;&nbsp;&nbsp;
                          <Typography
                            variant="body-2"
                            color={COLOR.surfaceNeutralBold['neutral-11']}
                          >
                            {e.question}
                          </Typography>
                          -
                          <Typography
                            style={{
                              color: answer?.answer
                                ? COLOR.surfaceNeutralBold['neutral-11']
                                : COLOR_CONFIG['neutral-text'],
                            }}
                            variant="body-2"
                          >
                            {answer?.answer || 'No answer'}
                          </Typography>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
